"use client";

import useMediaQuery from "@calcom/lib/hooks/useMediaQuery";
import classNames from "@calcom/ui/classNames";
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "@calcom/ui/components/hover-card";

const ZOOM_ON_HOVER =
  "transition-transform duration-500 ease-out group-hover/service-image:scale-110 motion-reduce:transition-none motion-reduce:group-hover/service-image:scale-100";

function HoverFrame({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={classNames(
        "pointer-events-none absolute inset-1 scale-110 rounded-md border border-white/90 opacity-0 transition duration-300 ease-out group-hover/service-image:scale-100 group-hover/service-image:opacity-100 group-focus-visible/service-image:scale-100 group-focus-visible/service-image:opacity-100 motion-reduce:transition-none",
        className
      )}
    />
  );
}

/**
 * `thumbnail` is a small square that shows the full image on hover, for lists.
 * `cover` is a full-width banner that is already large, so it has no hover preview.
 */
export function ServiceImage({
  imageUrl,
  thumbnailUrl,
  alt,
  variant = "thumbnail",
  className,
}: {
  imageUrl: string;
  thumbnailUrl: string;
  alt: string;
  variant?: "thumbnail" | "cover";
  className?: string;
}) {
  const isNarrow = useMediaQuery("(max-width: 640px)");

  if (variant === "cover") {
    return (
      <span
        className={classNames(
          "group/service-image border-subtle relative block aspect-[16/10] w-full overflow-hidden rounded-xl border",
          className
        )}>
        <img
          src={imageUrl}
          alt={alt}
          decoding="async"
          // The upper part of the photo usually holds the face or treated area
          className={classNames(
            "h-full w-full object-cover object-[50%_25%]",
            ZOOM_ON_HOVER,
            "group-hover/service-image:scale-105"
          )}
        />
        <HoverFrame className="inset-3" />
      </span>
    );
  }

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className={classNames(
            "group/service-image border-subtle relative block h-14 w-14 shrink-0 overflow-hidden rounded-lg border shadow-sm",
            className
          )}>
          <img
            src={thumbnailUrl}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={classNames("h-full w-full object-cover", ZOOM_ON_HOVER)}
          />
          <HoverFrame />
        </span>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side={isNarrow ? "bottom" : "left"}
          align={isNarrow ? "end" : "start"}
          sideOffset={12}
          collisionPadding={16}
          className="w-auto overflow-hidden rounded-xl p-1 shadow-xl duration-200">
          <span className="group/service-image relative block overflow-hidden rounded-lg">
            {/* Radix flips the preview to the roomier side but never shrinks it, so it is sized to the room left */}
            <img
              src={imageUrl}
              alt={alt}
              decoding="async"
              className={classNames(
                "block max-h-[min(420px,calc(var(--radix-hover-card-content-available-height)-0.5rem))] w-auto max-w-[min(420px,calc(var(--radix-hover-card-content-available-width)-0.5rem))] object-contain",
                ZOOM_ON_HOVER,
                "group-hover/service-image:scale-105"
              )}
            />
            <HoverFrame className="inset-3" />
          </span>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
